export const COMPLIANCE_FOCUSES = [
  'certs-expired',
  'certs-expiring',
  'medicals-expired',
  'medicals-due',
  'coe-soon',
  'intention-overdue',
  'nationalities',
] as const;

export type ComplianceFocus = (typeof COMPLIANCE_FOCUSES)[number];

export const COMPLIANCE_META: Record<
  ComplianceFocus,
  {
    title: string;
    body: string;
    statsBody: string;
    kpiLabel: string;
    empty: string;
    tone?: boolean;
    detailTab: string;
  }
> = {
  'certs-expired': {
    title: 'Certs expired',
    body: 'Certificates already past expiry.',
    statsBody: 'Count of expired certificates per rank, category, and days overdue.',
    kpiLabel: 'Certs expired',
    empty: 'No expired certificates',
    tone: true,
    detailTab: 'Certificates',
  },
  'certs-expiring': {
    title: 'Certs expiring',
    body: 'Certificates due within the selected window.',
    statsBody: 'Count of expiring certificates per rank, category, and days until expiry.',
    kpiLabel: 'Certs expiring',
    empty: 'No expiring certificates',
    tone: true,
    detailTab: 'Certificates',
  },
  'medicals-expired': {
    title: 'Medicals expired',
    body: 'Fitness / medicals past next-due date.',
    statsBody: 'Count of expired medicals per rank, kind, and days overdue.',
    kpiLabel: 'Medicals expired',
    empty: 'No expired medicals',
    tone: true,
    detailTab: 'Medicals',
  },
  'medicals-due': {
    title: 'Medicals due soon',
    body: 'Fitness / medicals due within the selected window.',
    statsBody: 'Count of medicals due soon per rank, kind, and days until due.',
    kpiLabel: 'Medicals due soon',
    empty: 'No medicals due soon',
    tone: true,
    detailTab: 'Medicals',
  },
  'coe-soon': {
    title: 'COE soon',
    body: 'Contract end of employment approaching within the selected window.',
    statsBody: 'Count of approaching COE per vessel, rank, and days to COE.',
    kpiLabel: 'COE soon',
    empty: 'No COE approaching',
    tone: true,
    detailTab: 'Crew',
  },
  'intention-overdue': {
    title: 'Intention overdue',
    body: 'Sign-off intention past due.',
    statsBody: 'Count of overdue sign-off intention per vessel, rank, and months onboard.',
    kpiLabel: 'Intention overdue',
    empty: 'No overdue intentions',
    tone: true,
    detailTab: 'Crew',
  },
  nationalities: {
    title: 'Nationalities',
    body: 'Distinct nationalities currently onboard.',
    statsBody: 'Count of onboard crew per nationality.',
    kpiLabel: 'Nationalities',
    empty: 'No onboard crew',
    detailTab: 'Crew',
  },
};

export function isComplianceFocus(v: string | undefined): v is ComplianceFocus {
  return v != null && Object.prototype.hasOwnProperty.call(COMPLIANCE_META, v);
}

export function isComplianceStatsFocus(
  v: string | undefined,
): v is Exclude<ComplianceFocus, 'intention-overdue' | 'nationalities' | 'coe-soon'> {
  return isComplianceFocus(v) && v !== 'intention-overdue' && v !== 'nationalities' && v !== 'coe-soon';
}

export function complianceReportPath(focus: ComplianceFocus): string {
  return `/crew/reports/compliance/${focus}`;
}

export function complianceStatsPath(
  focus: Exclude<ComplianceFocus, 'intention-overdue' | 'nationalities' | 'coe-soon'>,
): string {
  return `/crew/statistics/compliance/${focus}`;
}
